import { useEffect, useState } from 'react'
import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import styled from 'styled-components'

import { useSignIn } from '~/features/auth/queries/useAuth'
import { type LoginAuthProps, setAuthInfo } from '~/features/auth/utils/auth'
import Close from '~/shared/assets/icons/Close'
import Eye from '~/shared/assets/icons/Eye'
import Naver from '~/shared/assets/logos/naver.png'
import Button from '~/shared/components/buttons/Button'
import { checkEmailValidation } from '~/shared/components/modals/auth/checkEmailValidation'
import { isModalOpenState } from '~/shared/states'
import theme from '~/shared/styles/theme'

function SignInModal() {
  const navigate = useNavigate()
  const [_, setIsModalOpen] = useRecoilState(isModalOpenState)
  const { enqueueSnackbar } = useSnackbar()

  const { mutate: signInMutate } = useSignIn()

  const [emailInput, setEmailInput] = useState<string>('')
  const [passwordInput, setPasswordInput] = useState<string>('')
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false)
  const [errorMsg, setErrorMsg] = useState<string>('')

  const handleSignIn = () => {
    if (!emailInput) {
      setErrorMsg('이메일을 입력해주세요.')

      return
    }

    if (!passwordInput) {
      setErrorMsg('비밀번호를 입력해주세요.')

      return
    }

    signInMutate(
      {
        userInfo: {
          email: emailInput,
          password: passwordInput,
        },
      },
      {
        onSuccess: (response) => {
          if (response.errorCode < 0) {
            setErrorMsg(response.errorMsg)
          } else if (response.errorCode === 0) {
            if (response.data) {
              setAuthInfo({ data: response.data } as LoginAuthProps)
            }
            enqueueSnackbar('로그인이 완료되었습니다.', { variant: 'success' })
            setIsModalOpen({ contents: '', isOpen: false })
            navigate('/')
          }
        },
      },
    )
  }

  const enterkey = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const key = event.key || event.keyCode
    if (key === 'Enter' || key === 13) {
      handleSignIn()
    }
  }

  const signInNaver = async () => {
    window.location.href = `https://nid.naver.com/oauth2.0/authorize?response_type=code&client_id=${import.meta.env.VITE_NAVER_CLIENT_ID}&state=starlike&redirect_uri=${import.meta.env.VITE_REDIRECT_URI}`
  }

  const resetPassword = () => {
    setIsModalOpen({ contents: 'password-change', isOpen: true })
  }

  useEffect(() => {
    if (emailInput && !checkEmailValidation(emailInput)) {
      setErrorMsg('유효하지 않은 이메일입니다.')
    } else {
      setErrorMsg('')
    }
  }, [emailInput, passwordInput])

  return (
    <Wrapper onClick={(e) => e.stopPropagation()}>
      <CloseIcon
        onClick={() => setIsModalOpen({ contents: '', isOpen: false })}
      />
      <Title>로그인</Title>
      <InputBox>
        <WarningText>
          {errorMsg && `*${errorMsg}`}
          <br />
        </WarningText>
        <Input
          className={`${errorMsg && 'warning'}`}
          type="text"
          placeholder="Email"
          onChange={(e) => setEmailInput(e.target.value)}
        />
        <PasswordLabel>
          <Input
            type={isPasswordVisible ? 'text' : 'password'}
            placeholder="Password"
            onChange={(e) => setPasswordInput(e.target.value)}
            onKeyDown={enterkey}
          />
          <EyeIcon
            color={
              isPasswordVisible ? theme.colors.grey_5 : theme.colors.grey_4
            }
            onClick={() => setIsPasswordVisible(!isPasswordVisible)}
          />
        </PasswordLabel>
        <ButtonBox>
          <SignUpButton
            onClick={() =>
              setIsModalOpen({ contents: 'sign-up', isOpen: true })
            }
          >
            회원가입
          </SignUpButton>
          <ResetPasswordButton onClick={resetPassword}>
            비밀번호 재설정
          </ResetPasswordButton>
        </ButtonBox>
      </InputBox>
      <SignInButton
        $width={346}
        $height={48}
        $fontSize={18}
        $variant="primary"
        onClick={handleSignIn}
      >
        로그인
      </SignInButton>
      <NaverSignInButtonBox>
        <img src={Naver} alt="naver" width={42} height={42} />
        <NaverSignInButton onClick={signInNaver}>
          네이버 로그인
        </NaverSignInButton>
      </NaverSignInButtonBox>
    </Wrapper>
  )
}

export default SignInModal

const Wrapper = styled.section`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  display: flex;
  flex-direction: column;
  gap: 36px;
  align-items: center;

  box-sizing: border-box;
  width: 500px;
  height: 628px;
  margin: 0 auto;
  padding: 108px 77px 60px;

  background-color: ${theme.colors.white};
  border-radius: 12px;

  @media ${theme.device.medium} {
    width: 90%;
    height: auto;
    padding: 56px 20px 30px;
  }
`

const CloseIcon = styled(Close)`
  position: absolute;
  top: 36px;
  right: 36px;

  @media ${theme.device.medium} {
    top: 20px;
    right: 20px;
    width: 24px;
    height: 24px;
  }
`

const Title = styled.h1`
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 42px;
  font-weight: 500;

  @media ${theme.device.medium} {
    font-size: 36px;
  }
`

const InputBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`

const PasswordLabel = styled.label`
  position: relative;
`

const Input = styled.input`
  box-sizing: border-box;
  width: 100%;
  height: 49px;
  padding: 12px 20px;

  font-family: NanumSquareNeo-Light, sans-serif;
  font-size: 18px;

  background-color: ${theme.colors.grey_1};
  border: none;
  border-radius: 12px;

  &.warning {
    border: 1px solid ${theme.colors.warning};
  }

  @media ${theme.device.medium} {
    font-size: 15px;
  }
`

const WarningText = styled.div`
  gap: 10px;

  height: 20px;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 110%;
  color: ${theme.colors.warning};
  text-align: center;

  @media ${theme.device.medium} {
    font-size: 15px;
  }
`

const EyeIcon = styled(Eye)`
  cursor: pointer;

  position: absolute;
  top: 50%;
  right: 20px;
  transform: translateY(-50%);
`

const ButtonBox = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`

const SignUpButton = styled.button`
  cursor: pointer;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 18px;
  color: ${theme.colors.mint_3};

  background-color: transparent;
  border: none;

  @media ${theme.device.medium} {
    font-size: 15px;
  }
`

const ResetPasswordButton = styled.button`
  cursor: pointer;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 18px;
  color: ${theme.colors.grey_4};

  background-color: transparent;
  border: none;

  @media ${theme.device.medium} {
    font-size: 15px;
  }
`

const SignInButton = styled(Button)`
  width: 100%;
  height: 48px;

  font-size: 18px;
  color: ${theme.colors.white};

  background-color: ${theme.colors.grey_5};
`

const NaverSignInButtonBox = styled.div`
  cursor: pointer;

  display: flex;
  flex-direction: row;
  gap: 20px;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 82px;

  border-top: 1px solid ${theme.colors.grey_3};
`

const NaverSignInButton = styled.button`
  cursor: pointer;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 18px;
  color: ${theme.colors.grey_5};

  background-color: transparent;
  border: none;

  @media ${theme.device.medium} {
    font-size: 15px;
  }
`
